import {
  Flex,
  getPixelValue,
  IconButton,
  spacing,
  Text,
  useDelayedValue,
  useViewportSize,
} from '@lemonade-hq/uniclient-theme';
import { setElementVars } from '@vanilla-extract/dynamic';
import { clsx } from 'clsx';
import type { Variants } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';
import type { RefObject } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { FRAME_TRANSITION_DURATION, HISTORY_ITEM_OUT_DURATION } from '../animators/durations';
import type { HistoryEntry } from '../hooks/useWizardHistory';
import { EDIT_COMPLETION_DELAY_MS } from '../hooks/useWizardHistoryEdit';
import { FULLSTORY_MASK_CLASSNAME } from '../utils/consts';
import { parseFormattedTitle } from '../utils/parseFormattedTitle';
import { HistoryMessageBubble } from './HistoryMessageBubble';
import { MOBILE_HEADER_SAFE_AREA } from './layout';
import { hasVisibleHistoryItemVar, isHistoryVisibleVar, MAIN_GRID_COLUMNS } from './Wizard.css';
import { WIZARD_LEFT_SIDE_COLUMNS, WIZARD_RIGHT_SIDE_COLUMNS } from './WizardFrame.css';
import { HEADER_HEIGHT, headerHeightVar } from './WizardHeader.css';
import * as styles from './WizardHistory.css';
import { WizardHistoryOutput } from './WizardHistoryOutput';
import { WizardLayoutContainer } from './WizardLayoutContainer';
import { LAYOUT_COLUMN_GAP_MAP } from './WizardLayoutContainer.css';
import { WizardScrollSnapContainer } from './WizardScrollSnapContainer';
import { WizardSideGrid } from './WizardSideGrid';
import { TITLE_COLUMNS } from './WizardTitle';

const HeaderSpacer: React.FC = () => <div className={styles.headerSpacer} />;

const VISIBLE_HISTORY_ITEM_THRESHOLD = 0.8;

interface VariantsProps {
  readonly itemNumber: number;
  readonly isTransitioningOut: boolean;
}

const variants: Variants = {
  visible: ({ isTransitioningOut }: VariantsProps) => ({
    opacity: 1,
    transition: {
      duration: isTransitioningOut ? 0 : undefined,
      delay: FRAME_TRANSITION_DURATION / 1000,
    },
  }),
  hidden: () => ({
    opacity: 0,
    transition: {
      duration: HISTORY_ITEM_OUT_DURATION / 1000,
    },
  }),
};

interface WizardHistoryProps {
  readonly isTransitioningOut: boolean;
  readonly containerRef?: RefObject<HTMLDivElement>;
  readonly history: HistoryEntry[];
  readonly disabled: boolean;
  readonly editEnabled?: boolean;
  readonly onEdit: (stepName: string) => void;
  readonly onEditComplete?: () => void;
  readonly scrollToHistory?: () => void;
  readonly editingEntryId: string | null;
  readonly animationDelay: number;
}

export const WizardHistory = forwardRef<HTMLDivElement, WizardHistoryProps>(
  (
    {
      isTransitioningOut,
      history,
      disabled,
      onEdit,
      onEditComplete,
      editEnabled,
      containerRef,
      scrollToHistory,
      editingEntryId,
      animationDelay,
    },
    ref,
  ) => {
    const isInitialRender = useDelayedValue(false, true, animationDelay + FRAME_TRANSITION_DURATION * 2);
    const delayedIsTransitioningOut = useDelayedValue(isTransitioningOut, true, FRAME_TRANSITION_DURATION);
    const [hideIcon, setHideIcon] = useState(false);
    const { isBelowDesktopViewport } = useViewportSize();
    const [mobileEditedBubbleIndex, setMobileEditedBubbleIndex] = useState<number | null>(null);

    const internalOnEdit = useCallback(
      (entry: HistoryEntry) => () => {
        if (!entry.editable || !editEnabled) return;

        setHideIcon(true);
        onEdit(entry.id);
        setTimeout(() => {
          setHideIcon(false);
        }, EDIT_COMPLETION_DELAY_MS);
      },
      [editEnabled, onEdit],
    );

    useEffect(() => {
      if (containerRef?.current == null) return;

      setElementVars(containerRef.current, {
        [isHistoryVisibleVar]: disabled || history.length === 0 ? '0' : '1',
      });
    }, [containerRef, disabled, history.length]);

    const resetFirstVisibleHistoryItem = useCallback(() => {
      setMobileEditedBubbleIndex(null);
      const allHistoryItems = document.querySelectorAll<HTMLElement>(`[data-is-first-visible="true"]`);

      allHistoryItems.forEach(item => {
        delete item.dataset.isFirstVisible;
      });
    }, []);

    const onMobileEditedBubbleClick = useCallback(
      (index: number) => {
        resetFirstVisibleHistoryItem();
        setMobileEditedBubbleIndex(index);
      },
      [resetFirstVisibleHistoryItem],
    );

    useEffect(() => {
      resetFirstVisibleHistoryItem();

      const rootMargin = isBelowDesktopViewport
        ? `-${HEADER_HEIGHT.sm + MOBILE_HEADER_SAFE_AREA + getPixelValue(spacing.s48)}px 0px`
        : undefined;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            resetFirstVisibleHistoryItem();

            const target = entry.target as HTMLElement;

            if (entry.intersectionRatio > VISIBLE_HISTORY_ITEM_THRESHOLD) {
              target.dataset.isVisible = 'true';
            } else {
              delete target.dataset.isVisible;
            }

            const firstVisibleHistoryItem = document.querySelector<HTMLElement>(`[data-is-visible="true"]`);

            const hasVisibleHistoryItem = firstVisibleHistoryItem != null;

            if (hasVisibleHistoryItem) {
              firstVisibleHistoryItem.dataset.isFirstVisible = 'true';
            }

            setElementVars(document.body, {
              [hasVisibleHistoryItemVar]: hasVisibleHistoryItem ? '1' : '0',
            });
          });
        },
        {
          threshold: [0.2, 0.4, 0.6, 0.8, 1],
          rootMargin,
        },
      );

      const historyItems = document.querySelectorAll(`.${styles.historyItemWrapper}`);

      historyItems.forEach(item => {
        observer.observe(item);
      });

      return () => {
        observer.disconnect();
      };
    }, [history, isBelowDesktopViewport, resetFirstVisibleHistoryItem]);

    return (
      <>
        <WizardScrollSnapContainer
          className={styles.historyContainer}
          gap={LAYOUT_COLUMN_GAP_MAP}
          justifyContent="center"
          position="relative"
          ref={ref}
          scrollSnapAlign="end"
        >
          <WizardSideGrid columns={WIZARD_LEFT_SIDE_COLUMNS} />

          <AnimatePresence onExitComplete={onEditComplete}>
            {!disabled && (
              <motion.div
                // TODO: this is a hack due to an issue with how framer motion handles the translateY property with css variables
                // see https://github.com/motiondivision/motion/issues/3410
                animate={{
                  translateY: `calc(calc(-50% - ${headerHeightVar}) * 0)`,
                  transition: {
                    ease: 'easeOut',
                    delay: FRAME_TRANSITION_DURATION / 1000,
                    duration: isInitialRender ? undefined : 0,
                  },
                }}
                exit={{
                  translateY: `calc(calc(-100% - ${headerHeightVar}) * 1)`,
                  transition: {
                    duration: FRAME_TRANSITION_DURATION / 1000 - 0.05,
                  },
                }}
                initial={{
                  translateY: `calc(calc(-50% - ${headerHeightVar}) * 1)`,
                }}
                key="history"
              >
                <WizardLayoutContainer columns={MAIN_GRID_COLUMNS}>
                  <Flex
                    className={styles.history}
                    flexDirection="column"
                    flexGrow={1}
                    flexShrink={0}
                    gap={spacing.s32}
                    gridColumn={{
                      sm: `span ${MAIN_GRID_COLUMNS.sm}`,
                      md: `span ${MAIN_GRID_COLUMNS.md}`,
                      lg: `2 / span ${MAIN_GRID_COLUMNS.lg}`,
                    }}
                    height="fit-content"
                    justifyContent="start"
                    position="relative"
                  >
                    <AnimatePresence initial={false} mode="popLayout" onExitComplete={onEditComplete}>
                      {history.map((entry, index) => (
                        <Flex
                          animate="visible"
                          as={motion.div}
                          custom={{
                            itemNumber: history.length - (index + 1),
                            isTransitioningOut: delayedIsTransitioningOut,
                          }}
                          data-private
                          exit="hidden"
                          flexDirection="column"
                          gap={styles.HISTORY_TITLE_GAP}
                          initial="hidden"
                          // eslint-disable-next-line react/no-array-index-key
                          key={entry.name + index}
                          layout
                          onClick={scrollToHistory}
                          variants={variants}
                          width="100%"
                        >
                          <WizardLayoutContainer
                            columns={{
                              sm: MAIN_GRID_COLUMNS.sm,
                              md: TITLE_COLUMNS,
                            }}
                          >
                            <Text
                              className={styles.title}
                              gridColumn={{
                                sm: `span ${MAIN_GRID_COLUMNS.sm}`,
                                md: `span ${MAIN_GRID_COLUMNS.md}`,
                                lg: `span ${TITLE_COLUMNS}`,
                              }}
                              type="h5"
                            >
                              {parseFormattedTitle(entry.title).parsedNodes}
                            </Text>
                          </WizardLayoutContainer>
                          <Flex alignItems="center" justifyContent="flex-end" width="100%">
                            <Flex
                              alignItems="center"
                              className={clsx(FULLSTORY_MASK_CLASSNAME, styles.historyItemWrapper, {
                                [styles.editable]: editEnabled && entry.editable,
                                [styles.mobileEditedBubble]: mobileEditedBubbleIndex === index,
                              })}
                              gap={spacing.s08}
                            >
                              {editEnabled && entry.editable && (
                                <motion.div
                                  animate={{ opacity: hideIcon ? 0 : 1 }}
                                  initial={{ opacity: 1 }}
                                  transition={{ duration: 0.1, ease: 'easeOut' }}
                                >
                                  <IconButton
                                    background="floating"
                                    className={styles.editIcon}
                                    icon="edit"
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      internalOnEdit(entry)();
                                    }}
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        internalOnEdit(entry)();
                                      }
                                    }}
                                    role="button"
                                    size="md"
                                    tabIndex={0}
                                  />
                                </motion.div>
                              )}
                              {entry.value != null && entry.value.trim() !== '' && (
                                <HistoryMessageBubble
                                  index={index}
                                  isBeingEdited={editingEntryId === entry.id}
                                  onClick={isBelowDesktopViewport ? onMobileEditedBubbleClick : undefined}
                                  value={entry.value}
                                >
                                  <WizardHistoryOutput showEdit={false} value={entry.value} />
                                </HistoryMessageBubble>
                              )}
                            </Flex>
                          </Flex>
                        </Flex>
                      ))}
                    </AnimatePresence>
                  </Flex>
                </WizardLayoutContainer>
              </motion.div>
            )}
          </AnimatePresence>
          <WizardSideGrid columns={WIZARD_RIGHT_SIDE_COLUMNS} />
        </WizardScrollSnapContainer>
        <HeaderSpacer />
      </>
    );
  },
);
